import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AuthCallback from "./pages/AuthCallback";
import TherapistDashboard from "./pages/therapist/TherapistDashboard";
import Exercises from "./pages/therapist/Exercises";
import PlanEditor from "./pages/therapist/PlanEditor";
import PatientDashboard from "./pages/patient/PatientDashboard";
import AdminUsers from "./pages/Admin";
import { Toaster } from "@/components/ui/sonner";

const homeFor = (user) => (user?.role === "therapist" ? "/therapist" : "/dashboard");

function FullPageSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-950" data-testid="app-loading">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

function ProtectedRoute({ role, adminOnly, children }) {
  const { user } = useAuth();
  const location = useLocation();
  const effectiveUser = user || location.state?.user || null;
  if (effectiveUser === null) return <FullPageSpinner />;
  if (effectiveUser === false) return <Navigate to="/login" replace />;
  if (role && effectiveUser.role !== role) return <Navigate to={homeFor(effectiveUser)} replace />;
  if (adminOnly && !effectiveUser.is_admin) return <Navigate to={homeFor(effectiveUser)} replace />;
  return <Layout>{children}</Layout>;
}

function HomeRedirect() {
  const { user } = useAuth();
  if (user === null) return <FullPageSpinner />;
  if (user === false) return <Navigate to="/login" replace />;
  return <Navigate to={homeFor(user)} replace />;
}

function AppRouter() {
  const location = useLocation();
  // Fragment-Check synchron während des Renderns (verhindert Race Conditions)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/" element={<HomeRedirect />} />
      <Route
        path="/therapist"
        element={
          <ProtectedRoute role="therapist">
            <TherapistDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/therapist/exercises"
        element={
          <ProtectedRoute role="therapist">
            <Exercises />
          </ProtectedRoute>
        }
      />
      <Route
        path="/therapist/patients/:patientId"
        element={
          <ProtectedRoute role="therapist">
            <PlanEditor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute role="patient">
            <PatientDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly>
            <AdminUsers />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<HomeRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster richColors position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
